/**
 * Profil de vitesse par pente — EF-CAL-001.
 *
 * Le profil se réduit à une droite passant par la vitesse sur le plat et la
 * vitesse à +5 %, bornée par une vitesse minimale et une vitesse maximale.
 * Il n'existe plus de correction par pente entière (décision P2 de DECISIONS.md).
 */

import type { SpeedParameters } from '../core/parameters';

/** Pente en deçà de laquelle la vitesse de −15 % s'applique. */
export const MIN_PROFILE_GRADE_PERCENT = -15;

/** Pente au-delà de laquelle la vitesse de +15 % s'applique. */
export const MAX_PROFILE_GRADE_PERCENT = 15;

/**
 * Vitesse en km/h pour une pente exprimée en pourcentage.
 *
 * Une pente inférieure à −15 % utilise la vitesse de −15 % ; une pente
 * supérieure à +15 % utilise celle de +15 %. Le résultat est ensuite borné
 * entre la vitesse minimale et la vitesse maximale.
 */
export function speedAtGrade(speed: SpeedParameters, gradePercent: number): number {
  const grade = Math.min(MAX_PROFILE_GRADE_PERCENT, Math.max(MIN_PROFILE_GRADE_PERCENT, gradePercent));
  const raw = speed.referenceKmh + (grade * (speed.uphill5Kmh - speed.referenceKmh)) / 5;
  return Math.min(speed.maxKmh, Math.max(speed.minKmh, raw));
}

export interface SaturationGrades {
  /** Vrai lorsque la droite est horizontale : aucune saturation n'existe. */
  readonly constant: boolean;
  /** Pente en deçà de laquelle la vitesse maximale s'applique. */
  readonly maxSpeedGradePercent: number | null;
  /** Pente au-delà de laquelle la vitesse minimale s'applique. */
  readonly minSpeedGradePercent: number | null;
  /** Vrai lorsque la borne correspondante n'est jamais atteinte sur [−15, +15]. */
  readonly maxSpeedUnreached: boolean;
  readonly minSpeedUnreached: boolean;
}

/**
 * Pentes de saturation, recalculées à chaque modification d'une saisie.
 * Avec les valeurs initiales, elles valent −5,0 % et +6,7 %.
 */
export function saturationGrades(speed: SpeedParameters): SaturationGrades {
  const slope = (speed.uphill5Kmh - speed.referenceKmh) / 5;
  if (slope === 0) {
    return {
      constant: true,
      maxSpeedGradePercent: null,
      minSpeedGradePercent: null,
      maxSpeedUnreached: false,
      minSpeedUnreached: false,
    };
  }

  const maxGrade = (speed.maxKmh - speed.referenceKmh) / slope;
  const minGrade = (speed.minKmh - speed.referenceKmh) / slope;
  const outside = (grade: number): boolean =>
    grade < MIN_PROFILE_GRADE_PERCENT || grade > MAX_PROFILE_GRADE_PERCENT;

  return {
    constant: false,
    maxSpeedGradePercent: maxGrade,
    minSpeedGradePercent: minGrade,
    maxSpeedUnreached: outside(maxGrade),
    minSpeedUnreached: outside(minGrade),
  };
}

export interface GradeSpeedSample {
  readonly gradePercent: number;
  readonly speedKmh: number;
}

/** Vitesses obtenues pour chaque pente entière de −15 % à +15 %. */
export function gradeSpeedTable(speed: SpeedParameters): readonly GradeSpeedSample[] {
  const samples: GradeSpeedSample[] = [];
  for (let grade = MIN_PROFILE_GRADE_PERCENT; grade <= MAX_PROFILE_GRADE_PERCENT; grade += 1) {
    samples.push({ gradePercent: grade, speedKmh: speedAtGrade(speed, grade) });
  }
  return samples;
}
